import { useEffect, useState } from "react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import DashShell from "@/components/dash/DashShell";
import { Card } from "@/components/portal/ui";
import NotificationBell from "@/components/dash/NotificationBell";
import { OP_LINKS } from "./OperatorDashboard";

const field = "w-full px-3 py-2.5 rounded-lg border border-[#E5E1D8] bg-white text-sm text-[#1A1918] focus:border-[#9F634E] outline-none";

function Field({ label, children }) {
  return <label className="block"><span className="eyebrow text-[#8A998C] block mb-1.5">{label}</span>{children}</label>;
}

export default function OperatorProfile() {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  useSeo({ title: "Consultant Profile", noindex: true });
  useEffect(() => { http.get("/operator/profile").then((r) => setP(r.data || {})).catch(() => setP({})); }, []);

  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const body = (({ name, title, bio, photo, phone, email, support_hours, service_radius, virtual_available, in_home_available, overnight_available }) =>
        ({ name, title, bio, photo, phone, email, support_hours, service_radius, virtual_available, in_home_available, overnight_available }))(p);
      const { data } = await http.put("/operator/profile", body);
      setP(data);
      toast.success("Profile saved");
    } catch (e) { toast.error("Could not save profile"); } finally { setSaving(false); }
  };

  if (!p) return <DashShell brandLabel="Consultant Workspace" links={OP_LINKS("profile")} rightSlot={<NotificationBell />}><p className="text-[#6B665E]">Loading…</p></DashShell>;

  return (
    <DashShell brandLabel="Consultant Workspace" links={OP_LINKS("profile")} rightSlot={<NotificationBell />}>
      <div className="mb-5">
        <div className="eyebrow text-[#8A998C] mb-1">Consultant account</div>
        <h1 className="font-display text-3xl text-[#1A1918]">Your profile</h1>
        <p className="text-[#6B665E] text-sm mt-1">This is the Certified Infant Sleep Consultant assigned to the storefront. Public-facing fields appear on marketing pages.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-5 max-w-5xl">
        <Card className="p-6 lg:col-span-1 h-fit" data-testid="profile-preview">
          <div className="flex flex-col items-center text-center">
            {p.photo ? <img src={p.photo} alt={p.name} className="w-24 h-24 rounded-full object-cover mb-3" /> : <div className="w-24 h-24 rounded-full bg-[#F3EFE9] grid place-items-center text-[#9F634E] font-display text-2xl mb-3">{(p.name || "?").slice(0, 1)}</div>}
            <div className="font-display text-lg text-[#1A1918]">{p.name || "—"}</div>
            <div className="text-[#6B665E] text-xs">{p.title || "Certified Infant Sleep Consultant"}</div>
          </div>
          <Field label="Profile image URL">
            <input data-testid="profile-photo" className={field} value={p.photo || ""} onChange={(e) => set("photo", e.target.value)} placeholder="https://…" />
          </Field>
          <p className="text-[#A39E98] text-[11px] mt-2">Paste a hosted image URL (square works best).</p>
        </Card>

        <Card className="p-6 lg:col-span-2 space-y-4" data-testid="profile-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Name"><input data-testid="profile-name" className={field} value={p.name || ""} onChange={(e) => set("name", e.target.value)} /></Field>
            <Field label="Title"><input data-testid="profile-title" className={field} value={p.title || ""} onChange={(e) => set("title", e.target.value)} /></Field>
            <Field label="Email"><input data-testid="profile-email" className={field} value={p.email || ""} onChange={(e) => set("email", e.target.value)} /></Field>
            <Field label="Phone"><input data-testid="profile-phone" className={field} value={p.phone || ""} onChange={(e) => set("phone", e.target.value)} /></Field>
            <Field label="Support hours"><input className={field} value={p.support_hours || ""} onChange={(e) => set("support_hours", e.target.value)} /></Field>
            <Field label="Service radius"><input className={field} value={p.service_radius || ""} onChange={(e) => set("service_radius", e.target.value)} /></Field>
          </div>
          <Field label="Bio"><textarea data-testid="profile-bio" rows={4} className={field} value={p.bio || ""} onChange={(e) => set("bio", e.target.value)} /></Field>
          <div>
            <span className="eyebrow text-[#8A998C] block mb-2">Availability</span>
            <div className="flex flex-wrap gap-2">
              {[["virtual_available", "Virtual"], ["in_home_available", "In-home"], ["overnight_available", "Overnight"]].map(([k, l]) => (
                <button key={k} data-testid={`profile-avail-${k}`} onClick={() => set(k, !p[k])}
                  className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${p[k] ? "bg-[#667A66]/15 border-[#667A66]/50 text-[#4d6b4d]" : "bg-white border-[#E5E1D8] text-[#8A998C]"}`}>{l} · {p[k] ? "On" : "Off"}</button>
              ))}
            </div>
          </div>
          <button data-testid="profile-save" onClick={save} disabled={saving} className="bg-[#9F634E] text-white rounded-full px-6 py-2.5 text-sm font-medium hover:bg-[#8a5342] transition-colors disabled:opacity-50">{saving ? "Saving…" : "Save profile"}</button>
        </Card>
      </div>
    </DashShell>
  );
}
